import { LoginHero } from '@frontend/assets';
import { match } from '@frontend/duck-tape';
import { useLoginForm, validators } from '@frontend/forms';
import { useEffect, useFeatureIsOn } from '@frontend/react';
import {
  AppShell,
  Button,
  ClerkLoading,
  ClerkUnavailable,
  Divider,
  EnvironmentDialog,
  Image,
  PasswordInput,
  SSOButton,
  ScrollableContentPanel,
  Text,
  TextInput,
  Title,
  XStack,
  YStack,
  isUserCancellationError,
  showToast,
  useSSOLoadState,
  type OAuthStrategy,
} from '@frontend/web-react';
import { NavLink, UnauthenticatedAppHeader } from '@frontend/web/components';
import { isMcpFlow, useAnalytics, useAuthentication } from '@frontend/web/hooks';
import { AUTHENTICATED_HOME_ROUTE, Env, createFileRoute, redirect } from '@frontend/web/utils';
import { useTestIds } from '../hooks/useTestIds';

type LoginSearch = { redirect?: string };

const Login = () => {
  const ssoLoadState = useSSOLoadState();
  const navigate = Route.useNavigate();
  const searchParams = Route.useSearch() as LoginSearch;
  const { redirect } = searchParams;
  const {
    clearURLParamData,
    getURLParamData,
    isAuthenticated,
    isClientSubscribed,
    isLoading,
    isLoginLoading,
    login,
    setURLParamData,
    sso,
  } = useAuthentication();
  const { track } = useAnalytics();
  const { enabled: isSignupChangesEnabled } = useFeatureIsOn('is_signup_changes_enabled');
  const { enabled: ssoEnabled } = useFeatureIsOn('is_sso_sign_in_enabled');
  const { dirty, getControl, handleSubmit, isValid } = useLoginForm({
    onSubmit: (values) => {
      track({ event: 'login', topic: 'auth' });
      login({ ...values, handleRedirect: true, isSignupChangesEnabled, redirect });
    },
  });

  const logInTestIds = useTestIds(['/login.emailInput', '/login.passwordInput', '/login.submitLoginFormButton']);

  useEffect(() => {
    if (isLoading) {
      return;
    }
    if (isClientSubscribed && isAuthenticated) {
      navigate({ to: redirect || AUTHENTICATED_HOME_ROUTE });
    }
  }, [isLoading, isClientSubscribed, isAuthenticated, navigate, redirect]);

  const handleSSOSignIn = async (strategy: OAuthStrategy) => {
    try {
      clearURLParamData();
      if (redirect) {
        setURLParamData({ ...(getURLParamData() ?? {}), redirect });
      }
      await sso.startOAuthFlow({ strategy });
    } catch (error) {
      if (isUserCancellationError(error)) {
        return;
      }
      const providerName = strategy === 'oauth_apple' ? 'Apple' : 'Google';
      showToast({
        title: `Failed to sign in with ${providerName}. Please try again.`,
        variant: 'danger',
      });
    }
  };

  const panelContent = match(ssoLoadState)
    .with({ status: 'loading' }, () => <ClerkLoading />)
    .with({ status: 'failure' }, ({ onReload }) => <ClerkUnavailable onAction={onReload} />)
    .with({ status: 'success' }, () => (
      // Stops native form html tag from preventing submit for impersonation email inputs
      <form className="gap-y-md flex flex-col" noValidate onSubmit={handleSubmit}>
        <Title type="h5">Welcome back</Title>
        {ssoEnabled ? (
          <>
            <SSOButton
              disabled={!sso.isLoaded || sso.isOAuthRedirecting || isLoginLoading}
              isLoading={sso.activeProvider === 'oauth_google'}
              label="Sign in with Google"
              onClick={() => handleSSOSignIn('oauth_google')}
              provider="google"
            />
            <SSOButton
              disabled={!sso.isLoaded || sso.isOAuthRedirecting || isLoginLoading}
              isLoading={sso.activeProvider === 'oauth_apple'}
              label="Sign in with Apple"
              onClick={() => handleSSOSignIn('oauth_apple')}
              provider="apple"
            />
            <Divider label="or" labelPosition="center" />
          </>
        ) : null}
        <TextInput
          testId={logInTestIds['/login.emailInput']}
          {...getControl('email')}
          autoComplete="email"
          disabled={sso.isOAuthRedirecting}
          label={'Email'}
          type="email"
        />
        <PasswordInput
          testId={logInTestIds['/login.passwordInput']}
          {...getControl('password')}
          disabled={sso.isOAuthRedirecting}
        />
        <XStack>
          <NavLink accessibilityLabel="Forgot password" to="/forgot-password">
            Forgot password
          </NavLink>
        </XStack>
        <Button
          accessibilityLabel="Login"
          className="mt-md"
          disabled={!isValid || !dirty || sso.isOAuthRedirecting}
          fullWidth
          isForm
          isLoading={isLoginLoading}
          testId={logInTestIds['/login.submitLoginFormButton']}
        >
          Login
        </Button>
        <XStack gapX="sm">
          <Text>Not a member?</Text>
          <NavLink accessibilityLabel="Sign up" to="/wait-list">
            Sign up
          </NavLink>
        </XStack>
      </form>
    ))
    .exhaustive();

  return (
    <AppShell>
      <UnauthenticatedAppHeader />
      <XStack className="h-screen">
        <ScrollableContentPanel>{panelContent}</ScrollableContentPanel>
        <YStack className="bg-surfaceBrand hidden flex-1 justify-center md:flex">
          {/* eslint-disable-next-line tailwindcss/no-arbitrary-value */}
          <Image className="h-[500px] w-[960px]" source={LoginHero} />
        </YStack>
      </XStack>
      <EnvironmentDialog apiUrl={Env.VITE_API_BASE_URL} env={Env.VITE_ENV} />
    </AppShell>
  );
};

export const Route = createFileRoute('/login')({
  beforeLoad: async ({ context, search }) => {
    if (isMcpFlow()) {
      throw redirect({
        to: '/signup/mcp/complete',
      });
    }
    if (context.authentication?.isAuthenticated) {
      throw redirect({ to: (search as { redirect?: string }).redirect || AUTHENTICATED_HOME_ROUTE });
    }
  },
  component: Login,
  validateSearch: (search) =>
    validators
      .object({
        redirect: validators.encodedURI().optional(),
      })
      .parse(search),
});
